import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import Undo from './components/QuestionTable/Undo.jsx';
import { get, api } from '../../../lib/api';
import { useNotification } from '../../../shared/Notification/useNotification';

const tileTypes = {
  live: {
    queryKey: 'getLiveTiles',
    url: '/api/client_admin/reports/question_answer_tiles',
  },
  archived: {
    queryKey: 'getArchivedTiles',
    url: '/api/client_admin/reports/question_answer_tiles/archived',
  },
};

export const useGetTiles = ({ sortBy, sortDirection, type, campaignId }) => {
  const params = {
    sort_by: sortBy,
    sort_direction: sortDirection,
    campaign_id: campaignId,
  };
  const { queryKey, url } = tileTypes[type];

  const { data = { data: [] }, isLoading } = useQuery(
    [queryKey, params],
    () => get(url, params),
    {
      keepPreviousData: true,
    },
  );

  return { data: data.data, isLoading };
};

export const useGetQuestions = ({ tileId, status, sortBy, sortDirection }) => {
  const queryClient = useQueryClient();

  const placeholderData = () => {
    const initialData = {
      data: {
        questions: [],
        tile: {
          tileHeadline: '',
          assigne: '',
          newQuestions: 0,
          allQuestions: 0,
          contactedQuestions: 0,
          answeredQuestions: 0,
        },
      },
    };

    const questionsData = queryClient
      .getQueriesData({
        queryKey: ['questions', { tileId }],
      })
      .filter(data => data[1]);

    return questionsData.length > 0
      ? questionsData[questionsData.length - 1][1]
      : initialData;
  };

  const { data, isLoading } = useQuery(
    ['questions', { tileId, tab: status, sortBy, sortDirection }],
    () =>
      get('/api/client_admin/reports/questions', {
        tile_id: tileId,
        status,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }),
    {
      placeholderData: placeholderData(),
    },
  );

  return { questions: data.data.questions, isLoading, tile: data.data.tile };
};

const updateTabsOnQueryCache = ({
  newData,
  queryClient,
  activeTab,
  tileId,
  sortBy,
  sortDirection,
}) => {
  const tabs = ['all-questions', 'new', 'contacted', 'answered'];
  const inactiveTabs = tabs.filter(tab => tab !== activeTab);

  inactiveTabs.forEach(tab =>
    queryClient.setQueryData(
      ['questions', { tileId, tab, sortBy, sortDirection }],
      oldData => {
        return oldData
          ? { data: { ...oldData.data, tile: newData.tile } }
          : { data: newData };
      },
    ),
  );
};

const updateQuestionStatus = ({
  newStatus,
  questionId,
  tileId,
  activeTab,
  sortBy,
  sortDirection,
}) => {
  const data = {
    new_status: newStatus,
    tile_id: tileId,
    status: activeTab,
    sort_by: sortBy,
    sort_direction: sortDirection,
  };

  return api({
    method: 'patch',
    url: `/api/client_admin/reports/questions/${questionId}`,
    data,
  });
};

export const useUpdateQuestionStatus = ({
  tileId,
  activeTab,
  questionId,
  sortBy,
  sortDirection,
}) => {
  const { showDefault } = useNotification();
  const { t } = useTranslation('shared');
  const queryClient = useQueryClient();

  const { mutate } = useMutation(updateQuestionStatus, {
    onSuccess: ({ data: { data, message } }) => {
      queryClient.setQueryData(
        ['questions', { tileId, tab: activeTab, sortBy, sortDirection }],
        { data },
      );
      updateTabsOnQueryCache({
        newData: data,
        queryClient,
        activeTab,
        tileId,
        sortBy,
        sortDirection,
      });
      showDefault({
        content: function UndoNotification({ handleClose }) {
          return (
            <Undo
              message={message}
              questionId={questionId}
              activeTab={activeTab}
              tileId={tileId}
              sortBy={sortBy}
              sortDirection={sortDirection}
              handleClose={handleClose}
            />
          );
        },
        title: t('reports:questionsTable.title'),
        disappear: false,
        id: questionId,
      });
    },
  });

  const updateQuestionStatusMutation = ({ newStatus }) =>
    mutate({ tileId, questionId, newStatus, activeTab, sortBy, sortDirection });

  return { mutate: updateQuestionStatusMutation };
};

const undoQuestionStatus = ({
  questionId,
  tileId,
  activeTab,
  sortBy,
  sortDirection,
}) => {
  const data = {
    tile_id: tileId,
    status: activeTab,
    sort_by: sortBy,
    sort_direction: sortDirection,
  };

  return api({
    method: 'patch',
    url: `/api/client_admin/reports/questions/undo/${questionId}`,
    data,
  });
};

export const useUndoQuestionStatus = ({
  tileId,
  activeTab,
  sortBy,
  sortDirection,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['reports', 'shared']);
  const { showSuccess } = useNotification();

  const { mutate } = useMutation(undoQuestionStatus, {
    onSuccess: ({ data: { data } }) => {
      queryClient.setQueryData(
        ['questions', { tileId, tab: activeTab, sortBy, sortDirection }],
        { data },
      );
      updateTabsOnQueryCache({
        newData: data,
        queryClient,
        activeTab,
        tileId,
        sortBy,
        sortDirection,
      });
      showSuccess({
        content: t('reports:questionsTable.undoSuccess'),
        title: t('reports:questionsTable.title'),
      });
    },
  });

  const undoMutation = ({ questionId }) =>
    mutate({ questionId, activeTab, tileId, sortBy, sortDirection });

  return { mutate: undoMutation };
};
